import type { SourcingPlatform } from '@/infrastructure/FulfillmentAdminClient';
import { SOURCING_PLATFORM_LABELS } from '@/utils/fulfillmentSourcingDetail';

const SOURCE_LABELS: Readonly<Record<string, string>> = {
  PARTNERSHIP: 'Brand',
};

/** Keep backend source values on the wire while presenting the operator-facing route name. */
export const fulfillmentSourceLabel = (source: string): string => SOURCE_LABELS[source] ?? source.replace(/_/g, ' ');

/** V1 routes the mirror turns into a route of their own; on a JIT line they name no purchase platform. */
const NON_PLATFORM_OMS_ROUTES = ['existing', 'wholesale', 'brand'];

/**
 * The platform OMS Center recorded for a JIT line (Coupang, Naver, Other retail), read from the V1 line
 * the mirror keeps whole as the line's snapshot. Only a server that predates the line's own
 * `purchasePlatform` needs this; one that sends the field (SSD-7396) is read from that alone. A V2-native
 * line's snapshot carries no `sourcingRoute`, and an empty one means OMS Center had not decided yet, so
 * both answer undefined and keep the screen's own reading.
 */
export const recordedOmsPlatform = (line: {
  sourcingRoute?: string | null;
  snapshot?: unknown;
}): string | undefined => {
  if (line.sourcingRoute !== 'JIT' || typeof line.snapshot !== 'object' || line.snapshot === null) {
    return undefined;
  }
  const recorded = (line.snapshot as Record<string, unknown>)['sourcingRoute'];
  if (typeof recorded !== 'string' || recorded.trim() === '') {
    return undefined;
  }
  const platform = recorded.trim();
  const lowered = platform.toLowerCase();
  return NON_PLATFORM_OMS_ROUTES.some((prefix) => lowered.startsWith(prefix)) ? undefined : platform;
};

/** Where a line was bought, as the screens name it; nothing when the line records no platform. */
export const purchasePlatformLabel = (platform?: SourcingPlatform | null): string | undefined =>
  platform ? (SOURCING_PLATFORM_LABELS[platform] ?? platform) : undefined;

/**
 * The platform a line names: its own `purchasePlatform` when the server sends the field, even as null,
 * and what OMS Center recorded in the snapshot only from a server that predates it.
 */
export const linePlatformLabel = (line: {
  sourcingRoute?: string | null;
  snapshot?: unknown;
  purchasePlatform?: SourcingPlatform | null;
}): string | undefined =>
  line.purchasePlatform === undefined ? recordedOmsPlatform(line) : purchasePlatformLabel(line.purchasePlatform);

/** A JIT route with where it was bought, as every fulfillment screen prints it: `JIT (Coupang)`. */
export const jitWithPlatform = (platform: string | undefined): string => (platform ? `JIT (${platform})` : 'JIT');

/** A line's route as the lists print it; a JIT line names the platform it was bought on when one is known. */
export const lineRouteLabel = (route: string, platform: string | undefined): string =>
  route === 'JIT' ? jitWithPlatform(platform) : fulfillmentSourceLabel(route);
